import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface PageTitleValue {
  title: ReactNode | null;
  setTitle: (title: ReactNode | null) => void;
}

const PageTitleContext = createContext<PageTitleValue>({ title: null, setTitle: () => {} });

/**
 * Lets one screen put a heading in the shared top bar.
 *
 * <p>
 * Defaults to nothing, which is what every screen that does not opt in keeps showing. That default
 * is the point: this exists so one page can add a heading without every other page having to know
 * about it or change.
 * </p>
 */
export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<ReactNode | null>(null);
  const value = useMemo(() => ({ title, setTitle }), [title]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

/** Read by the top bar. */
export function usePageTitleValue() {
  return useContext(PageTitleContext).title;
}

